import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SignOutIcon from '@mui/icons-material/ExitToApp';
import { SideMenuItem, SideMenuItemChild } from './SideMenuItem';
import { getRootComponentPath, RootComponents } from '../pages/RootPage';
import { SignOutDialog } from './SignOutDialog';
import {
  useCheckNewConnection,
  UserProfileContext,
} from '@xolv-mybrightlink/mybrightlink';
import { NavLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  EsNorcalProviderGuard,
  PRIVACY_POLICY_URL,
  Routes,
  TERMS_CONDITIONS_URL,
  useAnalytics,
  METRICS_ARTICLES_ACTIVITY_ACCESS,
  METRICS_ARTICLES_PROPERTY_SOURCE,
  METRICS_ARTICLES_PROPERTY_SOURCE_SIDE_BAR,
  METRICS_EVENTS_ACTIVITY_ACCESS,
  METRICS_EVENTS_PROPERTY_SOURCE,
  METRICS_EVENTS_PROPERTY_SOURCE_SIDE_BAR,
  METRICS_DIM_ACTIVITY_ACCESS,
  METRICS_DIM_PROPERTIES_SOURCE,
  METRICS_DIM_PROPERTIES_SOURCE_SIDE_BAR,
  METRICS_COMMUNITY_ACCESS,
  METRICS_COMMUNITY_ACTIVITY_ACCESS_SOURCE,
  METRICS_COMMUNITY_ACTIVITY_ACCESS_SOURCE_SIDE_BAR,
  METRICS_SAVED_ACTIVITY_ACCESS,
  METRICS_PROFILE_ACCESS,
} from '@xolv-mybrightlink/common';
import { useResourcesMenuItems } from '../hooks/useResourcesMenuItems';

export const drawerWidth = 250;

export const SideDrawer = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);

  const hasNewConnection = useCheckNewConnection();

  const updateSignOutDialogOpen = useCallback(
    (open: boolean) => setSignOutDialogOpen(open),
    [],
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const [profile] = useContext(UserProfileContext);

  const resourcesChildItems = useResourcesMenuItems();

  const analytics = useAnalytics();

  const onItemClick = useMemo(
    () => (component: RootComponents) => {
      if (component === RootComponents.Articles) {
        analytics.trackAction(METRICS_ARTICLES_ACTIVITY_ACCESS, {
          [METRICS_ARTICLES_PROPERTY_SOURCE]:
            METRICS_ARTICLES_PROPERTY_SOURCE_SIDE_BAR,
        });
      } else if (component === RootComponents.Events) {
        analytics.trackAction(METRICS_EVENTS_ACTIVITY_ACCESS, {
          [METRICS_EVENTS_PROPERTY_SOURCE]:
            METRICS_EVENTS_PROPERTY_SOURCE_SIDE_BAR,
        });
      } else if (component === RootComponents.Dimension_Parent_Class) {
        analytics.trackAction(METRICS_DIM_ACTIVITY_ACCESS, {
          [METRICS_DIM_PROPERTIES_SOURCE]:
            METRICS_DIM_PROPERTIES_SOURCE_SIDE_BAR,
        });
      } else if (component === RootComponents.Community) {
        analytics.trackAction(METRICS_COMMUNITY_ACCESS, {
          [METRICS_COMMUNITY_ACTIVITY_ACCESS_SOURCE]:
            METRICS_COMMUNITY_ACTIVITY_ACCESS_SOURCE_SIDE_BAR,
        });
      } else if (component === RootComponents.Saved) {
        analytics.trackAction(METRICS_SAVED_ACTIVITY_ACCESS);
      }
    },
    [analytics],
  );

  const calculateNameFontSize = (textLength: number) => {
    if (textLength > 35) {
      return 10;
    } else if (textLength > 30) {
      return 12;
    } else if (textLength > 20) {
      return 14;
    }
    return 16;
  };

  const drawer = (
    <Stack
      direction='column'
      justifyContent='space-between'
      alignItems='start'
      sx={{
        overflow: 'hidden',
        height: '100vh',
        bgcolor: '#3b4559',
      }}
    >
      <Grid sx={{ width: '100%' }}>
        <Grid
          container
          direction='column'
          alignItems='center'
          justifyContent='center'
        >
          <NavLink
            to={Routes.authenticated}
            style={{
              marginBottom: '88px',
              marginTop: '44px',
            }}
          >
            <img
              src='/public/assets/images/logo_mbl_white.svg'
              style={{
                width: '151px',
                height: '24px',
              }}
            />
          </NavLink>
        </Grid>
        <Grid>
          <SideMenuItem
            titleKey='sidebar.menu.home'
            path={Routes.authenticated}
            iconPath='/public/assets/icons/ic_home.svg'
            end={true}
          />
          {/*
          hide CareMenuItem for Web 1.0
          <CareMenuItem />
          */}
          <SideMenuItem
            titleKey='sidebar.menu.resources'
            path={Routes.resources.index}
            iconPath='/public/assets/icons/ic_resources.svg'
          >
            {resourcesChildItems.map((component, index) => (
              <SideMenuItemChild
                path={Routes.resources.child(getRootComponentPath(component))}
                key={RootComponents[component]}
                onChildClick={() => onItemClick(component)}
                titleKey={'sidebar.menu.' + getRootComponentPath(component)}
                isLast={index === resourcesChildItems.length - 1}
              />
            ))}
          </SideMenuItem>
          <SideMenuItem
            titleKey='sidebar.menu.community'
            path={Routes.community.index}
            iconPath='/public/assets/icons/ic_community.svg'
            end={false}
            onClick={() => onItemClick(RootComponents.Community)}
            hasBadge={hasNewConnection}
          />
          <EsNorcalProviderGuard>
            <SideMenuItem
              titleKey='sidebar.menu.progress-portrait'
              path={Routes.progressPortrait.index}
              iconPath='/public/assets/icons/ic_progress_portrait.svg'
              end={false}
              onClick={() => onItemClick(RootComponents.ProgressPortrait)}
              additionalIcon={
                <Box
                  sx={{
                    position: 'absolute',
                    p: 1,
                    backgroundColor: '#D9338D',
                    borderBottomLeftRadius: 12,
                    color: 'primary.contrastText',
                    right: 0,
                    top: -4,
                  }}
                >
                  <Typography fontSize={10} fontWeight={500}>
                    New!
                  </Typography>
                </Box>
              }
            />
          </EsNorcalProviderGuard>
          <Divider
            sx={{
              borderStyle: 'dashed',
              ml: 10,
              mr: 5,
              borderColor: '#d8dade',
            }}
          />
          <SideMenuItem
            titleKey='sidebar.menu.saved'
            path={Routes.saved}
            onClick={() => onItemClick(RootComponents.Saved)}
            iconPath='/public/assets/icons/ic_saved.svg'
          />
          {/*<SideMenuItem*/}
          {/*  titleKey='sidebar.menu.session-feedback'*/}
          {/*  path={Routes.sessionFeedback}*/}
          {/*  iconPath='/public/assets/icons/ic_session_feedback.svg'*/}
          {/*/>*/}
        </Grid>
      </Grid>
      <Grid
        container
        direction='column'
        justifyContent='flex-end'
        alignItems='flex-start'
      >
        <Grid
          container
          direction='row'
          justifyContent='center'
          alignItems='center'
        >
          <NavLink
            end={false}
            onClick={() => analytics.trackAction(METRICS_PROFILE_ACCESS)}
            to={Routes.profile.index}
            style={{ display: 'block', width: '100%', textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    width: 4,
                    height: 24,
                    flexGrow: 0,
                    borderTopRightRadius: 2,
                    borderBottomRightRadius: 2,
                    backgroundColor: isActive ? '#307fe2' : 'transparent',
                    left: 0,
                    position: 'absolute',
                  }}
                />

                <Stack
                  direction={'row'}
                  sx={{
                    ml: 12,
                    mr: 6,
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <AccountCircleIcon
                    sx={{ color: isActive ? '#00b2a9' : '#d8dade' }}
                  />
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: isActive ? '600' : 'normal',
                      fontSize: calculateNameFontSize(
                        (profile?.fullName ?? '').length,
                      ),
                      maxWidth: 150,
                      wordWrap: 'break-word',
                      color: '#d8dade',
                    }}
                  >
                    {profile?.fullName || ''}
                  </Typography>
                </Stack>
              </Box>
            )}
          </NavLink>
        </Grid>
        <Button
          variant='outlined'
          size='large'
          data-testid='signOutButton'
          onClick={() => setSignOutDialogOpen(true)}
          disableElevation
          startIcon={<SignOutIcon />}
          sx={{
            mt: 6,
            mb: 8,
            ml: 12,
            borderRadius: 22,
            py: 2.5,
            px: 7.5,
            color: '#ec99c6',
            border: '1px solid #ec99c6',
          }}
        >
          <FormattedMessage id='sidebar.sign-out' />
        </Button>
        <Grid
          container
          direction='row'
          justifyContent='space-around'
          alignItems='center'
          sx={{
            mb: 5,
          }}
        >
          <Link
            rel='noopener'
            variant='caption'
            href={TERMS_CONDITIONS_URL}
            target='_blank'
            sx={{
              color: '#d8dade',
              textDecoration: 'none',
            }}
          >
            <FormattedMessage id='sidebar.menu.terms-of-use' />
          </Link>
          <Link
            rel='noopener'
            variant='caption'
            href={PRIVACY_POLICY_URL}
            target='_blank'
            sx={{
              color: '#d8dade',
              textDecoration: 'none',
            }}
          >
            <FormattedMessage id='sidebar.menu.privacy-policy' />
          </Link>
        </Grid>
      </Grid>
    </Stack>
  );

  return (
    <>
      <AppBar
        position='fixed'
        elevation={0}
        color='transparent'
        sx={{
          mr: 2,
          display: { sm: 'none' },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component='nav'
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              borderRight: 'solid 0.5px #d8dade',
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              borderRight: 'solid 0.5px #d8dade',
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <SignOutDialog
        open={signOutDialogOpen}
        setOpen={updateSignOutDialogOpen}
      />
    </>
  );
};
